// src/constants/metricHelpers.ts

import type { MinimalCatalogEntry } from "@/types/metrics";

// Metric units (merged here)
export type MetricUnit = 'dollar' | 'percent' | 'none';
export type CatalogUnit = 'usd' | 'count' | null | undefined;

export function unitFromCatalog(unit: CatalogUnit): MetricUnit {
  if (unit === 'usd') return 'dollar';
  if (unit === 'count') return 'none';
  return 'none';
}

export function resolveUnitForMetric(metricId: string, ratioIds?: Set<string>): MetricUnit {
  if (!metricId) return 'none';
  if (metricId.startsWith('pct_')) return 'percent';
  if (ratioIds && ratioIds.has(metricId)) return 'none';
  return 'none';
}

// Resolve unit for a metric key using catalog when available; falls back to local rules
export function resolveUnit(
  metricKey: string,
  catalog?: Array<{ metric_key?: string; unit?: CatalogUnit }> | null,
  ratioIds?: Set<string>
): MetricUnit {
  if (catalog && Array.isArray(catalog)) {
    const found = catalog.find((m) => m.metric_key === metricKey);
    if (found) {
      const u = unitFromCatalog(found.unit as CatalogUnit);
      // If catalog gives a concrete unit (usd/count), use it; otherwise fall back to heuristic (e.g., pct_ prefix)
      if (u !== 'none') return u;
    }
  }
  return resolveUnitForMetric(metricKey, ratioIds);
}

// Humanize fallback for ids when catalog display_name is unavailable
export const humanizeId = (id: string): string => {
  if (!id) return "";
  const spaced = id
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return spaced
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// Catalog-first label resolver for metric/ratio ids
export const resolveColumnLabel = (
  id: string,
  catalog?: MinimalCatalogEntry[] | null,
  fallbackHeaders?: Record<string, string>
): string => {
  const entry = (catalog || []).find((c) => c.metric_key === id);
  if (entry) {
    const candidate = entry.display_name || entry.label || entry.name;
    if (candidate && String(candidate).trim() !== "") return String(candidate);
  }
  if (fallbackHeaders && fallbackHeaders[id]) return fallbackHeaders[id];
  return humanizeId(id);
};

// Metrics tab helpers (moved here)
import type { ColumnOption } from "@/components/common/ColumnPicker";
import type { MetricInfo } from "@/types/metrics";
import { MetricKeys } from "@/constants/metrics";

export type Mode = 'current' | 'changes' | 'aggregates';

export interface GroupDef {
  id: string;
  label: string;
  options: ColumnOption[];
}

export function buildQuickFilters(mode: Mode) {
  if (mode === 'current') {
    return [
      { id: 'all', label: 'All' },
      { id: 'financial', label: 'Financial' },
      { id: 'network', label: 'Network Activity' },
      { id: 'defi', label: 'DeFi' },
      { id: 'fees', label: 'Fees' },
      { id: 'ratios', label: 'Ratios' },
      { id: 'derivatives', label: 'Derivatives' },
    ];
  }
  if (mode === 'changes') {
    return [
      { id: 'all', label: 'All' },
      { id: 'price', label: 'Price' },
    ];
  }
  return [
    { id: 'all', label: 'All' },
    { id: 'financialFlows', label: 'Financial Flows' },
    { id: 'networkFlows', label: 'Network Flows' },
  ];
}

export function buildGroups(
  mode: Mode,
  metricOptions: ColumnOption[],
  ratioOptions: ColumnOption[] | undefined,
  changeOptions: ColumnOption[] | undefined,
  presentFields: Set<string>,
  SUFFIXES: readonly string[],
  catalog: MetricInfo[] | undefined
): GroupDef[] {
  const labelFromCatalog = (metricKey: string): string => {
    const m = (catalog || []).find((c) => String(c.metric_key) === String(metricKey));
    return m?.display_name || metricKey;
  };
  if (mode === 'current') {
    const filterPresent = (opts: ColumnOption[]) =>
      presentFields && presentFields.size > 0
        ? opts.filter((o) => presentFields.has(String(o.id)))
        : opts;
    const groups: GroupDef[] = [];
    const metricOpts = filterPresent(metricOptions).map((o) => ({ id: o.id, label: labelFromCatalog(String(o.id)) }));
    const ratioOpts = filterPresent(ratioOptions || []).map((o) => ({ id: o.id, label: labelFromCatalog(String(o.id)) }));
    if (metricOpts.length) groups.push({ id: 'metrics', label: 'Metrics', options: metricOpts });
    if (ratioOpts.length) groups.push({ id: 'ratios', label: 'Ratios', options: ratioOpts });
    return groups;
  }
  if (mode === 'changes') {
    const suff = SUFFIXES;
    // Filter out funding rate metrics from changes table
    const filteredChangeOptions = (changeOptions || []).filter(
      (o) => o.id !== MetricKeys.FuturesFROIW && o.id !== MetricKeys.FuturesFRVolW
    );
    const groupsByMetric = filteredChangeOptions
      .map((o) => {
        const parentLabel = labelFromCatalog(String(o.id));
        const children = suff
          .map((s) => ({ id: `${o.id}_${s}`, label: s }))
          .filter((opt) => presentFields.has(String(opt.id)));
        return children.length > 0 ? { id: `changes:${o.id}`, label: parentLabel, options: children } : null;
      })
      .filter(Boolean) as GroupDef[];
    return groupsByMetric;
  }
  const flowIds = new Set((catalog || [])
    .filter((m) => m.metric_class === 'flow')
    .map((m) => m.metric_key)
  );
  const groupsByMetric = (metricOptions || [])
    .filter((o) => flowIds.has(String(o.id)))
    .map((o) => {
      const parentLabel = labelFromCatalog(String(o.id));
      const children = SUFFIXES
        .map((s) => ({ id: `${o.id}_${s}`, label: s }))
        .filter((opt) => presentFields.has(String(opt.id)));
      return children.length > 0 ? { id: `aggregates:${o.id}`, label: parentLabel, options: children } : null;
    })
    .filter(Boolean) as GroupDef[];
  return groupsByMetric;
}

export function applyPresetToSelection(
  mode: Mode,
  presetId: string,
  args: {
    groups: GroupDef[];
    presentFields: Set<string>;
    SUFFIXES: readonly string[];
    metricOptions: ColumnOption[];
    ratioOptions: ColumnOption[] | undefined;
    changeOptions: ColumnOption[] | undefined;
    catalog: MetricInfo[] | undefined;
  }
): Set<string> {
  const { groups, presentFields, SUFFIXES, metricOptions, ratioOptions, changeOptions, catalog } = args;
  if (presetId === 'all') {
    return new Set(groups.flatMap((g) => g.options.map((o) => String(o.id))).filter((id) => !!id));
  }
  if (mode === 'current') {
    const financial = [
      MetricKeys.PriceClose,
      MetricKeys.MarketCap,
      MetricKeys.FDV,
      MetricKeys.Volume,
      MetricKeys.Fees,
      MetricKeys.Revenue,
    ];
    const network = [
      MetricKeys.DAU,
      MetricKeys.TxCount,
    ];
    const defi = [MetricKeys.DexVolume];
    const derivatives = [MetricKeys.FuturesOI, MetricKeys.FuturesFROIW, MetricKeys.FuturesFRVolW];
    const feesRatios = new Set<string>([MetricKeys.Fees]);
    for (const m of catalog || []) {
      if (m.metric_class === 'ratio' && Array.isArray(m.depends_on) && m.depends_on.includes(MetricKeys.Fees)) {
        feesRatios.add(m.metric_key);
      }
    }
    let ids: string[] = [];
    if (presetId === 'financial') ids = financial;
    else if (presetId === 'network') ids = network;
    else if (presetId === 'defi') ids = defi;
    else if (presetId === 'derivatives') ids = derivatives;
    else if (presetId === 'fees') ids = Array.from(feesRatios);
    else if (presetId === 'ratios') ids = (ratioOptions || []).map((o) => String(o.id));
    const present = Array.from(presentFields);
    return new Set(ids.filter((id) => present.includes(id)));
  }
  if (mode === 'changes') {
    let bases: string[] = [];
    if (presetId === 'price') bases = [MetricKeys.PriceClose]; else bases = (changeOptions || []).map((o) => String(o.id));
    const wanted = new Set(bases.flatMap((b) => Array.from(SUFFIXES).map((s) => `${b}_${s}`)));
    return new Set(Array.from(presentFields).filter((k) => wanted.has(k)));
  }
  let bases: string[] = [];
  if (presetId === 'financialFlows') bases = [MetricKeys.Volume, MetricKeys.Fees, MetricKeys.Revenue];
  else if (presetId === 'networkFlows') bases = [MetricKeys.DAU, MetricKeys.TxCount];
  else bases = (metricOptions || []).map((o) => String(o.id));
  const wanted = new Set(bases.flatMap((b) => Array.from(SUFFIXES).map((s) => `${b}_${s}`)));
  return new Set(Array.from(presentFields).filter((k) => wanted.has(k)));
}


